import React, { useState, useCallback, useEffect, useRef, useMemo } from "react";
import {
  Badge,
  Tooltip,
  IconButton,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  FormControl,
  FormLabel,
  Textarea,
  Input,
  VStack,
  HStack,
  Button,
  Text,
  Box,
} from "@chakra-ui/react";
import { FiEdit } from "react-icons/fi";
import { DarwinTool, DarwinToolListWrapper, DarwinToolType } from "build-ts/darwin_agent";
import { getToolsList, updateTool } from "../../services/NewDarwinEvaServices";
import Table, { TableColumn, TableFilter, TablePagination } from "../common/Table";
import { getToolTypeColorForDarwinEva } from "utils/helperFuncUtils";
import { useNavigate } from "react-router-dom";


const ToolTable: React.FC = () => {
  const hasMounted = useRef(false);
  const navigate = useNavigate();

  // State for data
  const [tools, setTools] = useState<DarwinTool[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Pagination state
  const [pagination, setPagination] = useState<TablePagination>({
    total: 0,
    offset: 0,
    has_more: false,
  });
  const [limit] = useState(50);

  // Tool filters
  const [filters, setFilters] = useState<Record<string, string>>({
    search: "",
    type: "all",
  });
  const filtersRef = useRef(filters);

  // Keep ref in sync with state
  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);

  const [sortBy, setSortBy] = useState("created_date");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");

  // Edit modal state
  const { isOpen: isEditToolOpen, onOpen: onEditToolOpen, onClose: onEditToolClose } = useDisclosure();
  const [editingTool, setEditingTool] = useState<DarwinTool | null>(null);
  const [editFormData, setEditFormData] = useState({
    name: "",
    description: "",
    agentic_name: "",
    agentic_description: ""
  });
  const [isUpdating, setIsUpdating] = useState(false);

  // Fetch tools from Darwin API
  const fetchTools = useCallback(async (
      reset = false,
      currentSortBy = "created_date",
      currentSortOrder = "asc",
      currentFilters: Record<string, string> = filters,
      newOffset?: number
  ) => {
    try {
      if (reset) {
        setLoading(true);
      }
      setError(null);

      const currentOffset = newOffset !== undefined ? newOffset : (reset ? 0 : pagination.offset || 0);
      const params = new URLSearchParams({
        limit: limit.toString(),
        offset: currentOffset.toString(),
        sort_by: currentSortBy,
        sort_order: currentSortOrder,
      });

      if (currentFilters.search && currentFilters.search.trim()) {
        params.append("search_string", currentFilters.search);
      }
      if (currentFilters.type && currentFilters.type !== "all") {
        params.append("tool_type", currentFilters.type);
      }

      const result: DarwinToolListWrapper = DarwinToolListWrapper.fromJSON(await getToolsList(params));

      if (result.tools) {
        // Always replace the tools list, never append
        setTools(result.tools);

        if (result.total) {
          setPagination({
            total: result.total,
            has_more: (currentOffset + limit) < result.total,
            offset: currentOffset,
          });
        }
      } else {
        // API succeeded but no tools returned
        setTools([]);
        setPagination({
          total: 0,
          has_more: false,
          offset: currentOffset,
        });
      }
    } catch (err) {
      const errorMsg = `Network error: ${err instanceof Error ? err.message : "Unknown error"}`;
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  }, [limit, pagination.offset, filters]);

  // Load data on component mount
  useEffect(() => {
    if (!hasMounted.current) {
      hasMounted.current = true;
      const loadData = async () => {
        await fetchTools(true);
      };
      loadData();
    }
  }, [fetchTools]);

  // Table event handlers
  const handlePageChange = useCallback(async (offset: number) => {
    await fetchTools(false, sortBy, sortOrder, filters, offset);
  }, [fetchTools, sortBy, sortOrder, filters]);

  const handleSort = useCallback(async (newSortBy: string, newSortOrder: "asc" | "desc") => {
    setSortBy(newSortBy);
    setSortOrder(newSortOrder);
    await fetchTools(true, newSortBy, newSortOrder, filters, 0);
  }, [fetchTools, filters]);

  const handleFilterChange = useCallback((newFilters: Record<string, string>) => {
    setFilters(newFilters);
    // Also update the ref immediately
    filtersRef.current = newFilters;
  }, []);

  const handleApplyFilters = useCallback(async () => {
    await fetchTools(true, sortBy, sortOrder, filtersRef.current, 0);
  }, [fetchTools, sortBy, sortOrder]);

  const handleClearFilters = useCallback(async () => {
    const clearedFilters = { search: "", type: "all" };
    setFilters(clearedFilters);
    filtersRef.current = clearedFilters;
    setSortBy("created_date");
    setSortOrder("asc");
    await fetchTools(true, "created_date", "asc", clearedFilters, 0);
  }, [fetchTools]);

  const handleEditTool = (tool: DarwinTool) => {
    // For SKILL_TOOL, navigate to dedicated detail page instead of inline modal
    if (tool.header?.type === DarwinToolType[DarwinToolType.SKILL_TOOL] && tool.header?.id) {
      navigate(`/eva/tool/${tool.header.id}`);
      return;
    }

    setEditingTool(tool);
    setEditFormData({
      name: tool.header?.name || "",
      description: tool.header?.description || "",
      agentic_name: tool.agentic_name || "",
      agentic_description: tool.agentic_description || ""
    });
    onEditToolOpen();
  };

  const handleUpdateTool = async () => {
    if (!editingTool) return;

    setIsUpdating(true);
    const response = await updateTool(editingTool.header?.id, {
      header: {
        name: editFormData.name,
        description: editFormData.description,
      },
      agentic_name: editFormData.agentic_name,
      agentic_description: editFormData.agentic_description
    });

    // Only update the tool in the list if the update was successful
    if (response) {
      setTools(prevTools =>
        prevTools.map(tool =>
          tool.header?.id === editingTool.header?.id
            ? {
                ...tool,
                header: {
                  ...tool.header,
                  name: response.header?.name,
                  description: response.header?.description,
                  last_modified_date: response.header?.last_modified_date,
                },
                agentic_name: response.agentic_name,
                agentic_description: response.agentic_description
              }
            : tool
        )
      );
      onEditToolClose();
      setEditingTool(null);
    }

    setIsUpdating(false);
  };

  const handleEditFormChange = (field: string, value: string) => {
    setEditFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  // Get tool types from DarwinToolType enum
  const toolTypes = useMemo(() => {
    return Object.values(DarwinToolType).filter(
      value => typeof value === "string" && value !== "UNRECOGNIZED" && value !== "UNKNOWN_TOOL_TYPE"
    ) as string[];
  }, []);

  // Table configuration
  const tableColumns: TableColumn<DarwinTool>[] = [
    {
      key: "name",
      label: "Internal Name",
      sortable: true,
      className: "eva-name-column",
      render: (tool) => (
        <Tooltip label={tool.header?.name} placement="top">
          <Text fontSize="sm" color="gray.700" noOfLines={2} wordBreak="break-word" mb={0} whiteSpace="normal" fontWeight="600">
            {tool.header?.name}
          </Text>
        </Tooltip>
      ),
    },
    {
      key: "type",
      label: "Type",
      className: "eva-type-column",
      render: (tool) => {
        const toolType = tool.header?.type as keyof typeof DarwinToolType;
        return (
          <Badge colorScheme={getToolTypeColorForDarwinEva(DarwinToolType[toolType])}>
            {tool.header?.type || "Unknown"}
          </Badge>
        );
      },
    },
    {
      key: "is_response_tool",
      label: "Response?",
      className: "eva-is-response-tool-column",
      render: (tool) => (
        <Badge colorScheme={tool.header?.is_response_tool ? "green" : "red"} size="sm">
          {tool.header?.is_response_tool ? "True" : "False"}
        </Badge>
      ),
    },
    {
      key: "agentic_name",
      label: "Name to Agent",
      sortable: true,
      className: "eva-agentic-name-column",
      render: (tool) => (
        <Tooltip label={tool?.agentic_name} placement="top">
          <Text fontWeight="semibold" fontSize="sm" noOfLines={1} wordBreak="break-word" mb={0}>
            {tool?.agentic_name}
          </Text>
        </Tooltip>
      ),
    },
    {
      key: "agentic_description",
      label: "Description to Agent",
      sortable: true,
      className: "eva-agentic-description-column",
      render: (tool) => (
        <Tooltip label={tool?.agentic_description} placement="top">
           <Text fontSize="sm" color="gray.700" noOfLines={2} wordBreak="break-word" mb={0} whiteSpace="normal">
            {tool?.agentic_description}
          </Text>
        </Tooltip>
      ),
    },
  ];

  const tableFilters: TableFilter[] = [
    {
      key: "search",
      type: "text",
      label: "Search",
      placeholder: "Search tools...",
      width: "300px",
    },
    {
      key: "type",
      type: "select",
      label: "Type",
      options: [
        { value: "all", label: "All Types" },
        ...toolTypes.map((type: string) => ({ value: type, label: type })),
      ],
      width: "200px",
    },
  ];

  const rowActions = (tool: DarwinTool) => (
    <Tooltip label="Edit Tool">
      <IconButton
        icon={<FiEdit />}
        size="sm"
        variant="ghost"
        onClick={() => handleEditTool(tool)}
        aria-label="Edit tool"
      />
    </Tooltip>
  );

  return (
    <Box height="100%" overflowY="hidden">
      <Table
        data={tools}
        columns={tableColumns}
        loading={loading}
        error={error}
        pagination={pagination}
        limit={limit}
        onPageChange={handlePageChange}
        sortBy={sortBy}
        sortOrder={sortOrder}
        onSort={handleSort}
        filters={tableFilters}
        filterValues={filters}
        onFilterChange={handleFilterChange}
        onApplyFilters={handleApplyFilters}
        onClearFilters={handleClearFilters}
        rowActions={rowActions}
        getItemId={(tool) => tool.header?.id || ""}
        emptyMessage="No tools found"
        emptySubMessage="Try adjusting your search criteria"
      />

      {/* Tool Edit Modal */}
      <Modal isOpen={isEditToolOpen} onClose={onEditToolClose} isCentered>
        <ModalOverlay />
        <ModalContent className="eva-modal-content" maxW="80vw" maxH="90vh">
          <ModalHeader>Edit Tool</ModalHeader>
          <ModalCloseButton />
          <ModalBody className="eva-modal-body eva-small-scrollbar" overflow="auto">
            {editingTool && (
              <HStack spacing={6} align="stretch" height="100%">
                {/* Left Column - Editable Fields */}
                <VStack spacing={4} align="stretch" flex="1">
                  <FormControl>
                    <FormLabel fontSize="sm" fontWeight="600">Internal Name</FormLabel>
                    <Input
                      size="sm"
                      value={editFormData.name}
                      onChange={(e) => handleEditFormChange("name", e.target.value)}
                      placeholder="Enter tool name"
                    />
                  </FormControl>

                  <FormControl>
                    <FormLabel fontSize="sm" fontWeight="600">Description</FormLabel>
                    <Textarea
                      value={editFormData.description}
                      onChange={(e) => handleEditFormChange("description", e.target.value)}
                      placeholder="Enter tool description"
                      rows={4}
                      resize="none"
                    />
                  </FormControl>

                  {/* Highlighted Agent Fields */}
                  <Box
                    bg="blue.50"
                    borderColor="blue.400"
                    borderWidth="2px"
                    borderRadius="md"
                    p={4}
                  >
                    <VStack spacing={3} align="stretch">
                      <FormControl>
                        <FormLabel fontSize="sm" fontWeight="700" color="blue.700">Name to Agent</FormLabel>
                        <Input
                          size="md"
                          value={editFormData.agentic_name}
                          onChange={(e) => handleEditFormChange("agentic_name", e.target.value)}
                          placeholder="Enter agentic tool name"
                          bg="white"
                          fontSize="md"
                          fontWeight="600"
                        />
                      </FormControl>

                      <FormControl>
                        <FormLabel fontSize="sm" fontWeight="700" color="blue.700">Description to Agent</FormLabel>
                        <Textarea
                          value={editFormData.agentic_description}
                          onChange={(e) => handleEditFormChange("agentic_description", e.target.value)}
                          placeholder="Enter agentic tool description"
                          rows={8}
                          resize="none"
                          bg="white"
                          fontSize="md"
                          fontWeight="600"
                        />
                      </FormControl>
                    </VStack>
                  </Box>
                </VStack>

                {/* Right Column - JSON Schema and Response Flag */}
                <VStack spacing={4} align="stretch" flex="1" maxH="100%" maxW="70%">
                  <HStack>
                <FormControl>
                    <FormLabel fontSize="sm" fontWeight="600">Tool Type</FormLabel>
                    <Input
                      size="sm"
                      value={editingTool.header?.type || ""}
                      isReadOnly
                      bg="gray.50"
                      cursor="not-allowed"
                      color="gray.600"
                    />
                    </FormControl>
                  <FormControl>
                    <FormLabel fontSize="sm" fontWeight="600">Is Response Tool</FormLabel>
                    <Input
                      size="sm"
                      value={editingTool.header?.is_response_tool ? "True" : "False"}
                      isReadOnly
                      bg="gray.50"
                      cursor="not-allowed"
                      color="gray.600"
                      fontWeight="600"
                    />
                  </FormControl>
                  </HStack>
                  <FormControl display="flex" flexDirection="column">
                    <FormLabel fontSize="sm" fontWeight="600">Args Schema JSON</FormLabel>
                    <Box
                      bg="gray.900"
                      color="green.300"
                      borderRadius="md"
                      p={4}
                      overflow="auto"
                      fontFamily="monospace"
                      fontSize="sm"
                      whiteSpace="pre"
                      maxH="60vh"
                      minH="400px"
                    >
                      {editingTool.args_schema ?
                        JSON.stringify(JSON.parse(editingTool.args_schema), null, 2) :
                        "No schema available"}
                    </Box>
                  </FormControl>
                </VStack>
              </HStack>
            )}
          </ModalBody>
          <ModalFooter>
            <Button variant="outline" mr={3} onClick={onEditToolClose}>
              Cancel
            </Button>
            <Button
              colorScheme="purple"
              bg="purple.700"
              _hover={{ bg: "purple.600" }}
              onClick={handleUpdateTool}
              isLoading={isUpdating}
              loadingText="Updating..."
            >
              Update Tool
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default ToolTable;
